import { useEffect, useRef } from "react";
import type { Move } from "@fmjd/engine";

/** A move as played, with the clocks left after it. */
export interface PlayedMove extends Move {
  ply: number;
  notation: string;
  whiteMs: number;
  blackMs: number;
  /** Unknown for a move that arrived over the socket rather than the API. */
  thinkMs: number | null;
}

/**
 * The game score, in FMJD numeric notation, two plies to a numbered row.
 *
 * White moves first in draughts, so ply 1 is White's first move and every
 * row is complete except possibly the last.
 */
export function MoveList({
  moves,
  current,
  onSelect,
  result,
}: {
  moves: PlayedMove[];
  /** Shown after the last move once the game is over, as a score sheet ends. */
  result?: { score: string; reason: string } | null;
  /** The ply on the board; 0 is the starting position. */
  current: number;
  onSelect: (ply: number) => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  const active = useRef<HTMLButtonElement>(null);

  // Keep the current move in view by scrolling the list itself, never the
  // page: scrollIntoView would also yank the board out from under the reader.
  useEffect(() => {
    const list = box.current;
    const el = active.current;
    if (!list || !el) return;
    // The list is position: relative, so offsetTop is already relative to it.
    const top = el.offsetTop;
    if (top < list.scrollTop) list.scrollTop = top;
    else if (top + el.offsetHeight > list.scrollTop + list.clientHeight) {
      list.scrollTop = top + el.offsetHeight - list.clientHeight;
    }
  }, [current, moves.length]);

  const rows = [];
  for (let i = 0; i < moves.length; i += 2) {
    const white = moves[i]!;
    const black = moves[i + 1];
    rows.push(
      <div key={i} style={{ display: "contents" }}>
        <span style={num}>{i / 2 + 1}.</span>
        {cell(white)}
        {black ? cell(black) : <span />}
      </div>
    );
  }

  function cell(m: PlayedMove) {
    const on = m.ply === current;
    return (
      <button
        ref={on ? active : undefined}
        onClick={() => onSelect(m.ply)}
        title={m.thinkMs === null ? undefined : `${(m.thinkMs / 1000).toFixed(1)}s`}
        style={{
          ...moveBtn,
          background: on ? "#00695c" : "transparent",
          color: on ? "#fff" : "#222",
        }}
      >
        {m.notation}
      </button>
    );
  }

  return (
    <div
      ref={box}
      style={{
        position: "relative",
        maxHeight: 300,
        overflowY: "auto",
        border: "1px solid #e0e0e0",
        borderRadius: 6,
        padding: 6,
        background: "#fff",
      }}
    >
      {moves.length === 0 ? (
        <div style={{ color: "#888", fontSize: 13, padding: 4 }}>No moves yet</div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "2.6em 1fr 1fr",
            rowGap: 2,
            alignItems: "center",
          }}
        >
          {rows}
        </div>
      )}
      {result && (
        <div
          style={{
            borderTop: "1px solid #eee",
            marginTop: 6,
            paddingTop: 6,
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: 16, fontWeight: 700, fontFamily: "ui-monospace, monospace" }}>
            {result.score}
          </div>
          {result.reason && <div style={{ fontSize: 12, color: "#666" }}>{result.reason}</div>}
        </div>
      )}
    </div>
  );
}

const num: React.CSSProperties = {
  color: "#888",
  fontSize: 13,
  textAlign: "right",
  paddingRight: 6,
  fontFamily: "ui-monospace, monospace",
};

const moveBtn: React.CSSProperties = {
  textAlign: "left",
  padding: "2px 6px",
  fontSize: 14,
  fontFamily: "ui-monospace, monospace",
  border: "none",
  borderRadius: 4,
  cursor: "pointer",
};
